import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface SidebarExtensionProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  triggerRef: React.RefObject<HTMLElement>;
}

const SidebarExtension: React.FC<SidebarExtensionProps> = ({
  isOpen,
  onClose,
  title,
  children,
  triggerRef,
}) => {
  const [position, setPosition] = useState({ top: 0 });

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const contentHeight = 200; // Approximate height of content

    // Calculate if there's enough space below
    const spaceBelow = windowHeight - rect.bottom;
    const spaceAbove = rect.top;

    let top;
    if (spaceBelow >= contentHeight) {
      // Enough space below, align with trigger top
      top = rect.top;
    } else if (spaceAbove >= contentHeight) {
      // Not enough space below but enough above, align with trigger bottom minus content height
      top = Math.max(rect.bottom - contentHeight, 0);
    } else {
      // Not enough space either way, center in available space
      top = Math.max(windowHeight - contentHeight, 0) / 2;
    }

    setPosition({ top });
  }, [triggerRef]);

  // Debounced version of updatePosition
  const debouncedUpdatePosition = useCallback(() => {
    let timeoutId: NodeJS.Timeout;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updatePosition, 100);
    };
  }, [updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleResize = debouncedUpdatePosition();

    // Initial position
    updatePosition();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, updatePosition, debouncedUpdatePosition]);

  if (!isOpen) return null;

  const handleContentClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Don't close if clicking menu buttons or their dropdowns
    if (
      target.closest('[role="menuitem"]') ||
      target.closest('[data-state="open"]') ||
      target.getAttribute("aria-haspopup") === "menu"
    ) {
      return;
    }

    // Close if clicking a regular link or button
    if (
      target.closest("a") ||
      (target.closest("button") && !target.closest('[role="menu"]'))
    ) {
      onClose();
    }
  };

  const content = (
    <div
      className="fixed z-50"
      style={{
        top: position.top,
        left: 48, // Width of sidebar
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="ml-4">
        <div className="w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg rounded-md">
          <div className="p-2" onClick={handleContentClick}>
            <h4 className="font-medium mb-2">{title}</h4>
            {children}
          </div>
        </div>
      </div>

      {/* Invisible overlay to handle clicking outside */}
      <div
        className="fixed inset-0 z-[-1]"
        onClick={onClose}
        aria-hidden="true"
      />
    </div>
  );

  return createPortal(content, document.body);
};

export default SidebarExtension;
